import React, { useState, useCallback } from 'react';
import { useNavigate, Link, Navigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/apiService';
import axios from 'axios';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [lookingUpStudent, setLookingUpStudent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    collegeEmail: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    role: 'STUDENT',
    usn: '',
    department: '',
    semester: '',
    isEmailVerified: false,
    isInCsv: false, // Whether student data was found in CSV
    csvLookupDone: false // Whether CSV lookup has been performed
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Lookup student data from CSV when email is verified
  const lookupStudentData = useCallback(async (email) => {
    try {
      setLookingUpStudent(true);
      const response = await axios.get(`${API_BASE_URL}/auth/lookup-student?email=${encodeURIComponent(email)}`);
      
      if (response.data.isInCsv) {
        // Auto-fill from CSV
        setFormData(prev => ({
          ...prev,
          name: response.data.name,
          usn: response.data.usn,
          isInCsv: true,
          csvLookupDone: true
        }));
        
        // Also parse USN to get department and semester
        const usnData = parseUSN(response.data.usn);
        if (usnData.department && usnData.semester) {
          setFormData(prev => ({
            ...prev,
            department: usnData.department,
            semester: usnData.semester
          }));
        }
        
        toast.success('✓ Student data loaded from records!');
      } else {
        // Not in CSV - allow manual entry
        setFormData(prev => ({
          ...prev,
          isInCsv: false,
          csvLookupDone: true
        }));
        toast.info('Email not found in records. Please enter your details manually.');
      }
    } catch (error) {
      console.error('Student lookup error:', error);
      toast.warning('Could not verify student data. Please enter manually.');
      setFormData(prev => ({
        ...prev,
        isInCsv: false,
        csvLookupDone: true
      }));
    } finally {
      setLookingUpStudent(false);
    }
  }, [API_BASE_URL]);

  const handleVerifyWithGoogle = async () => {
    try {
      setVerifyingEmail(true);
      const response = await axios.get(`${API_BASE_URL}/auth/google/login-url?state=register-verify`);
      
      // Store current form data in sessionStorage
      sessionStorage.setItem('registerFormData', JSON.stringify(formData));
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Google verify error:', error);
      toast.error('Failed to verify with Google');
      setVerifyingEmail(false);
    }
  };

  // Handle OAuth email verification return
  React.useEffect(() => {
    const savedData = sessionStorage.getItem('registerFormData');
    const verifiedEmail = searchParams.get('verified_email');
    
    if (verifiedEmail && savedData) {
      const parsedData = JSON.parse(savedData);
      const email = decodeURIComponent(verifiedEmail);
      
      setFormData({
        ...parsedData,
        collegeEmail: email,
        isEmailVerified: true
      });
      
      toast.success('✓ Email verified with Google!');
      sessionStorage.removeItem('registerFormData');
      navigate('/register', { replace: true });
      
      // Automatically lookup student data from CSV
      lookupStudentData(email);
    }
  }, [searchParams, navigate, lookupStudentData]);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Get full email for submission
  const getFullEmail = (email) => {
    if (!email.includes('@')) {
      // Auto-append domain if only username provided
      return email + '.nc@cambridge.edu.in';
    }
    // Return the full email as entered
    return email;
  };

  // Parse USN and extract department & semester
  const parseUSN = (usn) => {
    if (!usn || usn.length !== 10) return { department: '', semester: '', error: '' };

    // Extract components: 1AJ23CS402
    // Position 0-2: College (1AJ) - MUST be "1AJ"
    // Position 3-4: Year (23)
    // Position 5-6: Department code (CS, CY, CI, CD, ME, EC)
    // Position 7-9: Roll number (001-399: Regular students, 400-999: Diploma/Lateral entry students)
    
    const collegeCode = usn.substring(0, 3).toUpperCase(); // "1AJ"
    
    // Validate - ONLY "1AJ" is valid
    if (collegeCode !== '1AJ') {
      return { 
        department: '', 
        semester: '', 
        error: `Invalid college code "${collegeCode}". Must start with "1AJ" for CITNC` 
      };
    }
    
    const yearCode = usn.substring(3, 5); // "23"
    const deptCode = usn.substring(5, 7).toUpperCase(); // "CS"
    const rollNumStr = usn.substring(7, 10); // "402"
    const rollNum = parseInt(rollNumStr); // 402

    // Validate roll number: Must be between 001 and 999
    if (rollNum === 0 || isNaN(rollNum)) {
      return { 
        department: '', 
        semester: '', 
        error: `Invalid roll number "${rollNumStr}". Roll number must be between 001 and 999` 
      };
    }

    // Calculate join year and validate
    const joinYear = 2000 + parseInt(yearCode); // 23 -> 2023
    const currentYear = 2025;
    
    // Validate year: Only reject future years (allow alumni from past years)
    if (joinYear > currentYear) {
      return { 
        department: '', 
        semester: '', 
        error: `Invalid year "${yearCode}" (${joinYear}). Year cannot be in the future` 
      };
    }

    // Map department codes
    const deptMap = {
      'CS': 'CS',
      'CY': 'CY', // Cyber Security - separate department
      'CI': 'CI', // Artificial Intelligence - separate department
      'CD': 'CD', // Data Science - separate department
      'ME': 'ME',
      'EC': 'EC'
    };

    const department = deptMap[deptCode] || '';
    
    // If department code is invalid, return empty
    if (!department) {
      return { 
        department: '', 
        semester: '', 
        error: `Invalid department code "${deptCode}". Valid codes: CS, CY, CI, CD, ME, EC` 
      };
    }

    // Roll number >= 400 indicates Diploma/Lateral entry students
    const isDiplomaStudent = rollNum >= 400;

    let semester = '';
    if (department) {
      if (isDiplomaStudent) {
        // Diploma/Lateral entry students: Start in SEM_3, study with previous batch
        const yearsFromJoin = currentYear - (joinYear - 1);
        const semNumber = (yearsFromJoin * 2) + 1; // Odd semester
        semester = `SEM_${Math.min(semNumber, 8)}`;
      } else {
        // Regular students (roll < 400): Start in SEM_1
        const yearsFromJoin = currentYear - joinYear;
        const semNumber = (yearsFromJoin * 2) + 1; // Odd semester
        semester = `SEM_${Math.min(semNumber, 8)}`;
      }
    }

    return { department, semester, error: '' };
  };

  // Handle USN change and auto-fill department & semester
  const handleUSNChange = (value) => {
    const usn = value.toUpperCase();
    setFormData({ ...formData, usn, department: '', semester: '', usnError: '' });

    // Auto-detect department and semester
    if (usn.length === 10) {
      const { department, semester, error } = parseUSN(usn);
      if (department && semester) {
        setFormData(prev => ({ ...prev, usn, department, semester, usnError: '' }));
        toast.success(`Auto-detected: ${department}, ${semester.replace('SEM_', 'Semester ')}`);
      } else {
        // Invalid USN format - show specific error
        setFormData(prev => ({ ...prev, usn, usnError: error }));
        toast.error(error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Enforce Google email verification
    if (!formData.isEmailVerified) {
      toast.error('Please verify your email with Google first');
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Validate passwords match
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        setLoading(false);
        return;
      }

      // Validate password length
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters long');
        setLoading(false);
        return;
      }

      // Ensure role is always STUDENT for public registration
      const dataToSubmit = { 
        ...formData, 
        role: 'STUDENT',
        collegeEmail: getFullEmail(formData.collegeEmail)
      };

      // Remove confirmPassword before sending
      delete dataToSubmit.confirmPassword;
      
      // Remove leading 0 from phone number if present
      if (dataToSubmit.phoneNumber.startsWith('0')) {
        dataToSubmit.phoneNumber = dataToSubmit.phoneNumber.substring(1);
      }

      await authService.register(dataToSubmit);
      
      // Email verification is disabled - always redirect to login
      toast.success('Registration successful! You can now login.');
      setTimeout(() => navigate('/login'), 1500);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.[0]?.msg ||
                          'Registration failed';
      
      // Check if user already exists
      if (error.response?.status === 400 && 
          (errorMessage.includes('already exists') || errorMessage.includes('User already exists'))) {
        toast.info('Account already exists. Redirecting to login...');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        toast.error(errorMessage);
        console.error('Registration error:', error.response?.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-[calc(100vh-80px)] bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🎓</div>
            <h2 className="text-3xl font-bold text-slate-900">Student Registration</h2>
            <p className="text-slate-600 mt-2">Create your student account to access CITNC ERP System</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 1. Email Field - First */}
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-2">
                College Email Address *
              </label>
              
              {!formData.isEmailVerified ? (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleVerifyWithGoogle}
                    disabled={verifyingEmail}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-primary bg-white hover:bg-blue-50 rounded-lg shadow-sm transition-all duration-200"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032 s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2 C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                    </svg>
                    <span className="text-slate-700 font-medium">
                      {verifyingEmail ? 'Verifying...' : 'Verify with Google'}
                    </span>
                  </button>
                  <p className="text-sm text-slate-600 text-center">
                    🔒 Google verification required to confirm your @cambridge.edu.in or @citnc.ac.in email
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.collegeEmail}
                      disabled
                      className="w-full px-4 py-3 pr-24 bg-green-50 border-2 border-green-300 rounded-lg text-slate-700 font-medium"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 text-green-600 font-semibold">
                      ✓ Verified
                    </div>
                  </div>
                  {lookingUpStudent && (
                    <p className="text-sm text-blue-600 animate-pulse">
                      🔍 Looking up student records...
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Only show remaining fields after email is verified and CSV lookup is done */}
            {formData.isEmailVerified && formData.csvLookupDone && (
              <>
                {/* 2. USN Field - Second (disabled if found in CSV) */}
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    USN (University Seat Number) *
                  </label>
                  <input
                    type="text"
                    name="usn"
                    required
                    value={formData.usn}
                    onChange={(e) => handleUSNChange(e.target.value)}
                    disabled={formData.isInCsv}
                    placeholder="1AJ23CS402"
                    maxLength={10}
                    className={`w-full px-4 py-3 border rounded-lg uppercase transition-shadow ${
                      formData.isInCsv 
                        ? 'bg-blue-50 border-blue-300 text-slate-700 font-medium cursor-not-allowed' 
                        : 'border-slate-300 focus:ring-2 focus:ring-primary focus:border-transparent'
                    }`}
                  />
                  {formData.isInCsv && (
                    <p className="text-sm text-blue-600 mt-1">
                      ✓ Auto-filled from student records
                    </p>
                  )}
                  {!formData.isInCsv && (
                    <p className="text-sm text-slate-500 mt-1">
                      Format: 1AJ23CS402 (Department & Semester will be auto-detected)
                    </p>
                  )}
                </div>

                {/* Show auto-detected values or error */}
                {formData.usn.length === 10 && !formData.isInCsv && (
                  <>
                    {formData.department && formData.semester ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-sm text-green-800">
                          ✓ <strong>Auto-detected:</strong> {formData.department} Department, {formData.semester.replace('SEM_', 'Semester ')}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-sm text-red-800">
                          ✗ <strong>Invalid USN!</strong> {formData.usnError || 'Check format: Must start with "1AJ", valid year (not future), and valid department code (CS/CY/CI/CD/ME/EC)'}
                        </p>
                      </div>
                    )}
                  </>
                )}

                {/* 3. Name Field - Third (disabled if found in CSV) */}
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    disabled={formData.isInCsv}
                    placeholder="John Doe"
                    className={`w-full px-4 py-3 border rounded-lg transition-shadow ${
                      formData.isInCsv 
                        ? 'bg-blue-50 border-blue-300 text-slate-700 font-medium cursor-not-allowed' 
                        : 'border-slate-300 focus:ring-2 focus:ring-primary focus:border-transparent'
                    }`}
                  />
                  {formData.isInCsv && (
                    <p className="text-sm text-blue-600 mt-1">
                      ✓ Auto-filled from student records
                    </p>
                  )}
                </div>

                {/* 4. Phone Number - Always Manual */}
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    required
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="9876543210 or 06361184577"
                    pattern="[0-9]{10,11}"
                    maxLength="11"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    10-digit phone number (leading 0 will be removed automatically)
                  </p>
                </div>

                {/* 5. Password - Always Manual */}
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Password *
                  </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password (min 6 characters)"
                  minLength="6"
                  className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 hover:text-slate-700 transition-colors"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
                  <p className="text-sm text-slate-500 mt-1">
                    Minimum 6 characters
                  </p>
                </div>

                {/* 6. Confirm Password - Always Manual */}
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Confirm Password *
                  </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter password"
                  minLength="6"
                  className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 hover:text-slate-700 transition-colors"
                >
                  {showConfirmPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Must match the password above
                </p>
              </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm text-blue-900 space-y-2">
                  <p><strong>📧 Important Information:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>You must use your official CITNC college email address</li>
                    <li>Examples: yourname@cambridge.edu.in or yourname.nc@cambridge.edu.in</li>
                    {formData.isInCsv && (
                      <li className="text-blue-700 font-semibold">✓ Your name and USN have been auto-filled from student records</li>
                    )}
                    {!formData.isInCsv && formData.csvLookupDone && (
                      <li className="text-amber-700 font-semibold">⚠ Your email was not found in records. Please enter your details manually.</li>
                    )}
                    <li>Create a strong password that you'll use to login</li>
                    <li>Remember your password - you can reset it using "Forgot Password" if needed</li>
                    <li>Staff accounts are created by administrators only</li>
                  </ul>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="spinner border-2 border-white border-t-transparent w-5 h-5"></div>
                    Creating Account...
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>
            </>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:text-primary-dark font-medium">
                Login here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
